import { translate } from '../../i18n';
import { el, icon } from '../../lib/dom';
import type { AppState } from '../../store/app';
import { formatPercent } from '../format';

/**
 * Glasnost in tišina (P2 §3.5) — DS `.slider-row`.
 *
 * Komponenta obstaja **dvakrat na zaslonu**: v medijski vrstici (od 720px
 * naprej) in v razdelku Zvok med nastavitvami. To ni podvojitev iz lenobe,
 * ampak posledica tega, česa v tej fazi še ni:
 *
 * ⚠️ P2 §3.5 predvideva drsnik na namizju v vrstici, na telefonu pa **v
 * razširjenem pogledu** predvajalnika. Razširjenega pogleda ni — čaka na DS
 * komponento ([D2 §38](../../../docs/04-delivery/D2-ds-requests.md)). Dokler
 * je tako, je edina pot do glasnosti na telefonu razdelek Nastavitve; brez
 * tega bi bili `↑` `↓` in `M` **funkcija samo za tipkovnico**, torej
 * nedostopna vsakomur brez nje.
 *
 * Obe kopiji berta isto stanje in pišeta v isti store — druga se posodobi
 * sama, ker gre sprememba čez `render()`.
 *
 * **Glasnost NI status** (CLAUDE.md, signal ≠ status): polnilo je `--primary`
 * prek `.slider-row` in nikoli `--success`/`--danger`. Tiho ni napaka.
 */

export interface VolumeDeps {
  /** Nova glasnost, 0–1. */
  onVolume(value: number): void;
  onMuteToggle(): void;
  /**
   * Ali je glasnost na tej platformi sploh programsko nastavljiva.
   *
   * ⛔ **Na iOS ni** — WebKit `HTMLMediaElement.volume` ignorira (glej
   * `AudioCapabilities.volume`). Drsnik, ki se premika in ne naredi ničesar,
   * je slabši od drsnika, ki ga ni: uporabnik napako išče v svojih slušalkah.
   * Prijavljeno z naprave 2026-07-21 kot »slider za volume je dekoracija«.
   *
   * **Tišina ostane**, ker `muted` deluje povsod — tudi na iOS.
   */
  volumeSupported(): boolean;
}

export interface VolumeNode {
  root: HTMLElement;
  update(state: AppState): void;
}

/** Korak drsnika v odstotkih. Isti korak imata `↑` in `↓` (`ui/keyboard.ts`). */
export const VOLUME_STEP = 5;

export function createVolume(deps: VolumeDeps, id: string): VolumeNode {
  const mute = el('button', { class: 'icon-trigger ghost nw-volume__mute', type: 'button' });
  mute.addEventListener('click', deps.onMuteToggle);

  const input = el('input', {
    class: 'nw-volume__slider',
    type: 'range',
    id,
    name: id,
    min: '0',
    max: '100',
    step: String(VOLUME_STEP),
    value: '100',
  });
  input.addEventListener('input', () => {
    deps.onVolume(Number(input.value) / 100);
  });

  const value = el('span', { class: 'val tnum' });

  const root = el('div', { class: 'slider-row nw-volume' }, [mute, input, value]);

  return {
    root,
    update(state) {
      const { lang } = state;
      const { volume, muted } = state.settings;

      /**
       * Kjer glasnost ni nastavljiva, drsnika in odčitka **ni** — ostane gumb
       * za tišino, ki tam deluje. Odpoved ni tiha: razlog pove opis vrstice v
       * Nastavitvah (`settings.volume.unsupported`), enako kot pri EQ (P2 §6.5).
       */
      const supported = deps.volumeSupported();
      input.toggleAttribute('hidden', !supported);
      value.toggleAttribute('hidden', !supported);
      root.classList.toggle('nw-volume--mute-only', !supported);
      if (!supported) {
        const muteOnly = translate(lang, muted ? 'player.unmute' : 'player.mute');
        mute.setAttribute('aria-label', muteOnly);
        mute.title = muteOnly;
        mute.replaceChildren(icon(muted ? 'i-volume-x' : 'i-volume', 20));
        return;
      }

      // Utišano kaže **0 %**, ne shranjene glasnosti: odčitek mora ustrezati
      // temu, kar se sliši. Drsnik ohrani položaj, ker je to vrednost, na
      // katero se zvok vrne — in prav to je pomen gumba za tišino.
      const percent = Math.round(volume * 100);
      input.value = String(percent);
      value.textContent = formatPercent(lang, muted ? 0 : volume);

      input.setAttribute('aria-label', translate(lang, 'player.volume'));
      input.setAttribute('aria-valuetext', formatPercent(lang, volume));

      /**
       * ⚠️ **Ime se spreminja, `aria-pressed` ga NE spremlja** (P2 §9,
       * merilo 4.1.2). Oboje hkrati bralnik zaslona objavi dvakrat —
       * »utišaj, pritisnjeno« — in uporabnik ne ve, ali je to stanje ali
       * dejanje. Ista izbira kot pri gumbu predvajaj/ustavi.
       */
      const label = translate(lang, muted ? 'player.unmute' : 'player.mute');
      mute.setAttribute('aria-label', label);
      mute.title = label;
      mute.replaceChildren(icon(muted ? 'i-volume-x' : 'i-volume', 20));
    },
  };
}
